import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bell, CheckCircle2, X } from 'lucide-react';
import ChatModel from 'pages/chat-model';
import MobileNavBar from 'components/MobileNavBar';
import { LanguageToggle } from 'components/LanguageToggle/LanguageToggle';
import { userRoleData } from 'redux_store/slices/userRole';
import { roles } from 'constants/constant';
import {
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
} from 'redux_store/services/notifications';
import { useTranslation } from 'react-i18next';
import { formatTimeDifferenceUTC } from 'constants/updatedAt';
import { EventEnums, NotificationTypeMap } from 'pages/notification/constant';

const MobileLayout = ({ children }) => {
  const { t } = useTranslation(['navigation']);
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useSelector(userRoleData);
  const [showTopNotifications, setShowTopNotifications] = useState(false);
  const isB2BMobile = useMemo(
    () =>
      [
        roles.loanOfficer,
        roles.loanProcessor,
        roles.organization,
        roles.branchAdmin,
        roles.superAdmin,
      ].includes(userRole),
    [userRole]
  );
  const isConsumerMobile = userRole === roles.consumer;
  const showMobileHeader = isB2BMobile || isConsumerMobile;

  const { currentData: notificationData } = useGetNotificationsQuery(
    { showUnread: false },
    {
      pollingInterval: 60000,
      skip: !showMobileHeader,
    }
  );
  const [updateNotification] = useUpdateNotificationsMutation();

  const notificationList = notificationData?.data?.data ?? [];

  const unreadCount =
    notificationList?.filter((item) => item?.isRead === '0')?.length ?? 0;

  useEffect(() => {
    setShowTopNotifications(false);
  }, [location.pathname]);

  const handleMarkAsRead = async (notificationId) => {
    await updateNotification({
      data: { notification_ids: [notificationId] },
    });
  };

  const handleNotificationClick = async (notification) => {
    const { typeId, applicationId, userNotificationId, isRead } = notification;
    if (isRead === '0') {
      await updateNotification({
        data: { notification_ids: [userNotificationId] },
      });
    }
    const eventType = NotificationTypeMap[typeId];
    if (eventType) {
      switch (eventType) {
        case EventEnums.CONSUMER_PROFILE_ID_CREATED:
          navigate('/profile');
          break;
        case EventEnums.CONSUMER_PROFILE_COMPLETED:
        case EventEnums.CONSUMER_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVED:
          navigate('/pipeline');
          break;
        case EventEnums.CONSUMER_NOT_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVAL_REJECTION:
          navigate('/profile');
          break;
        case EventEnums.LOAN_OFFICER_CONSUMER_COMPLETED_ONBOARDING:
        case EventEnums.LOAN_OFFICER_PROFILE_COMPLETED:
        case EventEnums.LOAN_OFFICER_ADD_LEAD:
        case EventEnums.LOAN_OFFICER_ELIGIBLE:
        case EventEnums.LOAN_OFFICER_NOT_ELIGIBLE:
          navigate(`/pipeline/lead?applicationId=${applicationId}`);
          break;
        case EventEnums.LOAN_OFFICER_PRE_APPROVED:
        case EventEnums.LOAN_OFFICER_PRE_APPROVAL_REJECTION:
          navigate(`/pipeline/lead?applicationId=${applicationId}`);
          break;
        default:
          break;
      }
    }
    setShowTopNotifications(false);
  };

  return (
    <div className="relative flex h-screen h-dvh min-h-0 flex-col overflow-hidden bg-[#f9fafb]">
      {showMobileHeader && (
        <header className="shrink-0 border-b border-[#d8dfeb] bg-white px-3 pb-2 pt-[calc(env(safe-area-inset-top)+8px)]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <img
                src="/fundmoreai.png"
                alt="fundmore.ai"
                className="h-7 w-7 rounded-md object-contain"
              />
              <span className="truncate text-[16px] font-semibold leading-none text-[#2f3e63]">
                fundmore.ai
              </span>
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle variant="sidebar" forceExpanded />
              <button
                type="button"
                className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[#d8dfeb] bg-white text-[#3c588b]"
                onClick={() => setShowTopNotifications((prev) => !prev)}
                aria-label={t('navigation:notifications', {
                  defaultValue: 'Notifications',
                })}
              >
                <Bell className="h-[18px] w-[18px]" />
                {unreadCount > 0 && (
                  <span className="absolute right-[7px] top-[7px] h-2 w-2 rounded-full bg-[#3c588b]" />
                )}
              </button>
            </div>
          </div>
        </header>
      )}
      {showMobileHeader && showTopNotifications && (
        <>
          <button
            type="button"
            className="absolute inset-0 z-[84] bg-black/10"
            onClick={() => setShowTopNotifications(false)}
            aria-label={t('common:buttons.close', {
              defaultValue: 'Close',
              keySeparator: false,
            })}
          />
          <div className="absolute left-3 right-3 top-[calc(env(safe-area-inset-top)+56px)] z-[85] rounded-2xl border border-[#d8dfeb] bg-white shadow-[0_20px_45px_-24px_rgba(15,23,42,0.45)]">
            <div className="flex items-center justify-between border-b border-[#e7ebf2] px-3 py-2">
              <div className="flex items-center gap-2 text-[#112968]">
                <Bell className="h-4 w-4" />
                <span className="text-[13px] font-semibold">
                  {t('navigation:notifications', { defaultValue: 'Notifications' })}
                </span>
              </div>
              <button
                type="button"
                className="rounded-md p-1 text-[#7a8097] hover:bg-[#f3f5fa]"
                onClick={() => setShowTopNotifications(false)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[42vh] overflow-y-auto px-2 py-2">
              {notificationList.length === 0 && (
                <p className="py-3 text-center text-[12px] text-[#7a8097]">
                  {t('common:noNewNotifications', {
                    defaultValue: 'You have no new notifications at this time.',
                  })}
                </p>
              )}
              {notificationList.map((notification) => {
                const isUnread = notification.isRead === '0';
                const subject =
                  notification.subject ||
                  t('common:notification', { defaultValue: 'Notification' });
                const message = notification.message || '';
                const timeLabel = notification?.createdAt
                  ? formatTimeDifferenceUTC(notification.createdAt)
                  : '';
                return (
                  <div
                    key={notification.userNotificationId}
                    className={`mb-1 rounded-lg px-2 py-1.5 ${
                      isUnread ? 'bg-[#f2f6ff]' : 'bg-transparent'
                    }`}
                  >
                    <button
                      type="button"
                      className="w-full text-left"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="mb-0.5 flex items-center justify-between gap-2">
                        <p className="truncate text-[12px] font-semibold text-[#112968]">
                          {subject}
                        </p>
                        <span className="shrink-0 text-[10px] text-[#7a8097]">
                          {timeLabel}
                        </span>
                      </div>
                      <p className="line-clamp-2 text-[11px] text-[#7a8097]">
                        {message}
                      </p>
                    </button>
                    {isUnread && (
                      <button
                        type="button"
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-[#3c588b]"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMarkAsRead(notification.userNotificationId);
                        }}
                      >
                        {t('common:buttons.markAsRead', {
                          defaultValue: 'Mark as read',
                        })}
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      <main
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-[calc(68px+env(safe-area-inset-bottom)+14px)]"
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorY: 'contain' }}
      >
        {children}
      </main>
      <MobileNavBar />
      <ChatModel />
    </div>
  );
};

export default MobileLayout;
